import { Vector3 } from '@babylonjs/core/Maths/math';
import { VertexBuffer } from '@babylonjs/core/Meshes/buffer';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import { SubMesh } from '@babylonjs/core/Meshes/subMesh';
import { IndicesArray, Nullable } from '@babylonjs/core/types';
import { KbGeometry } from '@models/classes';
import { SubmeshData } from '@models/classes/features/submesh-feature';

export class SubmeshVertexData extends VertexData {
    /** Array of linked index segments, i.e. 0,1,1,2,2,3. A break in the link corresponds to a new path. */
    paths?: Nullable<IndicesArray>;
    /** Array of pairs of numbers, the first is the hole identifier, and the second is the index at which the hole starts in the path array */
    holes?: Nullable<IndicesArray>;
    submeshData?: Nullable<SubmeshData[]>;

    constructor() {
        super();
        this.indices = null;
        this.positions = null;
        this.normals = null;
        this.tangents = null;
        this.submeshData = null;
    }

    /**
     * @param kbGeometry the kbGeometry to copy the vertex data from.
     * Note that this copipes the vertex data by reference, so mutating the data WILL modify the kbGeometry.
     */
    static FromGeometry(kbGeometry: KbGeometry) {
        const result = new SubmeshVertexData();
        result.indices = kbGeometry.indices || null;
        result.positions = kbGeometry.positions || null;
        result.normals = kbGeometry.normals || null;
        result.tangents = kbGeometry.tangents || null;
        result.uvs = kbGeometry.uvs || null;
        result.submeshData = [
            {
                materialIndex: 0,
                verticesStart: 0,
                verticesCount: kbGeometry.positions ? kbGeometry.positions.length / 3 : 0,
                indexStart: 0,
                indexCount: kbGeometry.indices ? kbGeometry.indices.length : 0,
            },
        ];

        return result;
    }

    static ExtractFromMesh(mesh: Mesh, copyWhenShared?: boolean | undefined, forceCopy?: boolean | undefined) {
        const result = new SubmeshVertexData();
        const vertexData = super.ExtractFromMesh(mesh, copyWhenShared, forceCopy);

        SubmeshVertexData.CopyToRef(vertexData, result);
        if (mesh.subMeshes) {
            result.submeshData = extractSubmeshData(mesh.subMeshes);
        }
        if (mesh.metadata && mesh.metadata['paths']) {
            result.paths = copyWhenShared ? mesh.metadata['paths'].slice() : mesh.metadata['paths'];
        }
        if (mesh.metadata && mesh.metadata['holes']) {
            result.holes = copyWhenShared ? mesh.metadata['holes'].slice() : mesh.metadata['holes'];
        }

        return result;
    }

    static CopyToRef(source: Partial<VertexData | SubmeshVertexData>, target: SubmeshVertexData) {
        target.positions = source.positions || null;
        if ('paths' in source) {
            target.paths = source.paths || null;
        }
        if ('holes' in source) {
            target.holes = source.holes || null;
        }
        target.normals = source.normals || null;
        target.tangents = source.tangents || null;
        target.uvs = source.uvs || null;
        target.uvs2 = source.uvs2 || null;
        target.uvs3 = source.uvs3 || null;
        target.uvs4 = source.uvs4 || null;
        target.uvs5 = source.uvs5 || null;
        target.uvs6 = source.uvs6 || null;
        target.colors = source.colors || null;
        target.matricesIndices = source.matricesIndices || null;
        target.matricesWeights = source.matricesWeights || null;
        target.matricesIndicesExtra = source.matricesIndicesExtra || null;
        target.matricesWeightsExtra = source.matricesWeightsExtra || null;
        target.indices = source.indices || null;
        target.submeshData = (source as SubmeshVertexData).submeshData;
    }

    static ExtendToRef(source: Partial<VertexData | SubmeshVertexData>, target: SubmeshVertexData) {
        if (source.positions !== undefined) target.positions = source.positions;
        if ('paths' in source && source.paths !== undefined) target.paths = source.paths;
        if ('holes' in source && source.holes !== undefined) target.holes = source.holes;
        if (source.normals !== undefined) target.normals = source.normals;
        if (source.tangents !== undefined) target.tangents = source.tangents;
        if (source.uvs !== undefined) target.uvs = source.uvs;
        if (source.uvs2 !== undefined) target.uvs2 = source.uvs2;
        if (source.uvs3 !== undefined) target.uvs3 = source.uvs3;
        if (source.uvs4 !== undefined) target.uvs4 = source.uvs4;
        if (source.uvs5 !== undefined) target.uvs5 = source.uvs5;
        if (source.uvs6 !== undefined) target.uvs6 = source.uvs6;
        if (source.colors !== undefined) target.colors = source.colors;
        if (source.matricesIndices !== undefined) target.matricesIndices = source.matricesIndices;
        if (source.matricesWeights !== undefined) target.matricesWeights = source.matricesWeights;
        if (source.matricesIndicesExtra !== undefined) target.matricesIndicesExtra = source.matricesIndicesExtra;
        if (source.matricesWeightsExtra !== undefined) target.matricesWeightsExtra = source.matricesWeightsExtra;
        if (source.indices !== undefined) target.indices = source.indices;
        if ((source as SubmeshVertexData).submeshData !== undefined) {
            target.submeshData = (source as SubmeshVertexData).submeshData;
        }
    }

    deepClone() {
        const result = new SubmeshVertexData();
        result.indices = this.indices ? this.indices.slice() : null;
        result.positions = this.positions ? this.positions.slice() : null;
        result.paths = this.paths ? this.paths.slice() : null;
        result.holes = this.holes ? this.holes.slice() : null;
        result.normals = this.normals ? this.normals.slice() : null;
        result.tangents = this.tangents ? this.tangents.slice() : null;
        result.uvs = this.uvs ? this.uvs.slice() : null;
        result.submeshData = this.submeshData ? JSON.parse(JSON.stringify(this.submeshData)) : undefined;

        return result;
    }

    shallowClone() {
        const result = new SubmeshVertexData();
        SubmeshVertexData.CopyToRef(this, result);
        return result;
    }

    copyFrom(source: Partial<SubmeshVertexData>) {
        SubmeshVertexData.CopyToRef(source, this);
    }

    merge(
        others: SubmeshVertexData | SubmeshVertexData[],
        use32BitsIndices?: boolean,
        forceCloneIndices?: boolean,
        mergeMaterialIds?: boolean,
        enableCompletion?: boolean
    ): SubmeshVertexData {
        if (!Array.isArray(others)) {
            others = [others];
        }

        let pathIdxOffset = this.paths ? this.paths.length : 0;
        let segmentIdxOffset = 0;
        let vertexIdxOffset = this.positions ? this.positions.length / 3 : 0;
        const newPaths: number[] = [];
        const newHoles: number[] = [];

        if (this.paths) {
            for (let i = 2; i < this.paths.length; i += 2) {
                if (this.paths[i] !== this.paths[i - 1]) {
                    segmentIdxOffset++;
                }
            }
            if (this.holes) {
                segmentIdxOffset -= this.holes.length / 2;
            }
            segmentIdxOffset++;
        }

        others.map(other => {
            if (other.holes) {
                for (let i = 0; i < other.holes.length; i += 2) {
                    newHoles.push(other.holes[i] + segmentIdxOffset, other.holes[i + 1] + pathIdxOffset);
                }
                segmentIdxOffset -= other.holes.length / 2;
            }
            if (other.paths) {
                for (let i = 0; i < other.paths.length; i++) {
                    newPaths.push(other.paths[i] + vertexIdxOffset);
                }
                for (let i = 2; i < other.paths.length; i += 2) {
                    if (other.paths[i] !== other.paths[i - 1]) {
                        segmentIdxOffset++;
                    }
                }
                segmentIdxOffset++;
            }

            vertexIdxOffset += other.positions ? other.positions.length / 3 : 0;
            pathIdxOffset += other.paths ? other.paths.length : 0;
        });

        super.merge(others, use32BitsIndices, true, mergeMaterialIds, enableCompletion);

        if (newHoles.length) {
            if (!this.holes) {
                this.holes = new Int32Array();
            }
            if ('push' in this.holes) {
                for (let i = 0; i < newHoles.length; i++) {
                    this.holes.push(newHoles[i]);
                }
            } else {
                const newArr: Int32Array = new Int32Array(this.holes.length + newHoles.length);
                newArr.set(this.holes);
                newArr.set(newHoles, this.holes.length);
                this.holes = newArr;
            }
        }
        if (newPaths.length) {
            if (!this.paths) {
                this.paths = new Int32Array();
            }
            if ('push' in this.paths) {
                for (let i = 0; i < newPaths.length; i++) {
                    this.paths.push(newPaths[i]);
                }
            } else {
                const newArr: Int32Array = new Int32Array(this.paths.length + newPaths.length);
                newArr.set(this.paths);
                newArr.set(newPaths, this.paths.length);
                this.paths = newArr;
            }
        }
        return this;
    }

    applyToMesh(mesh: Mesh, updatable = true, writeSubmeshes = true) {
        if (!this.submeshData && writeSubmeshes && mesh.subMeshes && mesh.subMeshes.length > 1) {
            this.submeshData = extractSubmeshData(mesh.subMeshes);
        }
        const result = super.applyToMesh(mesh, updatable);
        if (this.paths) {
            if (!mesh.metadata) {
                mesh.metadata = {};
            }
            mesh.metadata['paths'] = this.paths.slice();
        }
        if (this.holes) {
            if (!mesh.metadata) {
                mesh.metadata = {};
            }
            mesh.metadata['holes'] = this.holes.slice();
        }
        if (mesh.geometry) {
            // Applying geometry doesn't remove vertex types if they are unset so remove them manually
            if (!this.normals) {
                mesh.geometry.removeVerticesData(VertexBuffer.NormalKind);
            }
            if (!this.uvs) {
                mesh.geometry.removeVerticesData(VertexBuffer.UVKind);
            }
        }

        if (writeSubmeshes) {
            if (this.submeshData) {
                mesh.subMeshes = [];
                this.submeshData.forEach(submesh => {
                    // SubMeshes add themselves to the mesh
                    new SubMesh(
                        submesh.materialIndex,
                        submesh.verticesStart,
                        submesh.verticesCount,
                        submesh.indexStart,
                        submesh.indexCount,
                        mesh
                    );
                });
            }
        } else {
            this.submeshData = extractSubmeshData(mesh.subMeshes);
        }

        return result;
    }
}

function extractSubmeshData(subMeshes: SubMesh[]): SubmeshData[] {
    return subMeshes.map(submesh => {
        return {
            materialIndex: submesh.materialIndex,
            verticesStart: submesh.verticesStart,
            verticesCount: submesh.verticesCount,
            indexStart: submesh.indexStart,
            indexCount: submesh.indexCount,
        };
    });
}

/**
 * @param vertexData
 * @returns null if geometry is valid, string if invalid
 */
export function verifyGeometry(vertexData: SubmeshVertexData): null | 'vertices' | 'submeshes' {
    let outputError: null | 'vertices' | 'submeshes' = null;
    if (vertexData.indices && vertexData.positions && vertexData.indices.length > 0) {
        const indicesCount = vertexData.indices.length;
        const vertexCount = vertexData.positions.length / 3;

        if (vertexData.submeshData) {
            vertexData.submeshData.forEach(submesh => {
                if (
                    submesh.indexStart + submesh.indexCount > indicesCount ||
                    submesh.verticesStart + submesh.verticesCount > vertexCount
                ) {
                    outputError = 'submeshes';
                }
            });
        }

        if (!outputError) {
            const c = vertexData.indices.length;
            let maxVertex = -1;
            for (let i = 0; i < c; i++) {
                maxVertex = Math.max(maxVertex, vertexData.indices[i]);
            }
            if (maxVertex > vertexData.positions.length / 3) {
                outputError = 'vertices';
            }
        }
    }

    return outputError;
}

export function getPolygonNormal(_points: Vector3[] | number[] | Float32Array) {
    const normal = Vector3.Zero();
    if (!_points) {
        return normal;
    }

    if (typeof _points[0] === 'number') {
        const points = _points as number[] | Float32Array;
        if (points.length < 9) {
            return normal;
        }

        const p1 = Vector3.Zero();
        const p2 = Vector3.Zero();

        let i = 0;
        while (p1.length() === 0 && points.length > i + 5) {
            p1.set(points[i + 3], points[i + 4], points[i + 5]);
            p1.subtractFromFloatsToRef(points[i], points[i + 1], points[i + 2], p1);
            i += 3;
        }

        p2.set(points[i], points[i + 1], points[i + 2]);
        while (normal.length() === 0 && points.length > i + 3) {
            p2.set(points[i + 3], points[i + 4], points[i + 5]);
            p2.subtractFromFloatsToRef(points[i], points[i + 1], points[i + 2], p2);
            Vector3.CrossToRef(p1, p2, normal);
            i += 3;
        }
    } else {
        const points = _points as Vector3[];
        if (points.length < 3) {
            return normal;
        }
        const p1 = Vector3.Zero();
        const p2 = Vector3.Zero();

        let i = 0;
        while (p1.length() === 0 && points.length > i + 1) {
            p1.copyFrom(points[i + 1]);
            p1.subtractInPlace(points[i]);
            i++;
        }

        while (normal.length() === 0 && points.length > i + 1) {
            p2.copyFrom(points[i + 1]);
            p2.subtractInPlace(points[i]);
            Vector3.CrossToRef(p1, p2, normal);
            i++;
        }
    }

    return normal.normalize();
}
